import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RefreshCw, Plus, Trash2, Loader2, Bell, ScrollText, Satellite, FileText } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  OK: "bg-emerald-100 text-emerald-700",
  PENDIENTE: "bg-slate-100 text-slate-500",
  ERROR_RFFM: "bg-rose-100 text-rose-700",
  ERROR_PARSE: "bg-rose-100 text-rose-700",
};

const QUEUE_BADGE = {
  PENDIENTE: "bg-slate-100 text-slate-600",
  PROCESANDO: "bg-blue-100 text-blue-700",
  COMPLETADO: "bg-emerald-100 text-emerald-700",
  ERROR: "bg-rose-100 text-rose-700",
};

const PROGRESS_LABEL = {
  PENDIENTE: "Pendiente",
  ANALIZANDO_FICHA: "Analizando ficha",
  BUSCANDO_TEMPORADAS: "Buscando temporadas",
  ANALIZANDO_PARTIDOS: "Analizando partidos",
  ANALIZANDO_ACTAS: "Analizando actas",
  CALCULANDO_ESTADISTICAS: "Calculando estadísticas",
  COMPLETADO: "Completado",
  ERROR: "Error",
};

export default function RffmEngine() {
  const [teams, setTeams] = useState([]);
  const [efTeams, setEfTeams] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [queue, setQueue] = useState({ counts: {}, jobs: [] });
  const [tab, setTab] = useState("equipos");
  const [busy, setBusy] = useState(null);
  const [form, setForm] = useState({ kind: "rival", name: "", rffm_team_code: "", rival_actual: "", equipo_ef: "", team_id: "" });

  const load = () => {
    api.get("/rffm/teams").then((r) => setTeams(r.data)).catch(() => {});
    api.get("/rffm/alerts").then((r) => setAlerts(r.data)).catch(() => {});
    api.get("/rffm/logs").then((r) => setLogs(r.data)).catch(() => {});
    api.get("/rffm/reports/queue").then((r) => setQueue(r.data)).catch(() => {});
  };
  useEffect(() => { load(); api.get("/teams").then((r) => setEfTeams(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (tab !== "informes") return undefined;
    const t = setInterval(() => api.get("/rffm/reports/queue").then((r) => setQueue(r.data)).catch(() => {}), 5000);
    return () => clearInterval(t);
  }, [tab]);

  const generatePending = async () => {
    setBusy("pending");
    try {
      const { data } = await api.post("/rffm/reports/generate-pending");
      toast.success(`${data.encolados} jugadores encolados para informe individual`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(null); }
  };

  const addTeam = async (e) => {
    e.preventDefault();
    try {
      await api.post("/rffm/teams", { ...form, team_id: form.team_id || null });
      toast.success("Equipo registrado en el motor RFFM");
      setForm({ kind: "rival", name: "", rffm_team_code: "", rival_actual: "", equipo_ef: "", team_id: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const syncTeam = async (t) => {
    setBusy(t.id);
    try {
      const { data } = await api.post(`/rffm/teams/${t.id}/sync`, {}, { timeout: 120000 });
      if (data.status === "OK") {
        toast.success(t.kind === "rival"
          ? `${t.name}: ${data.total} jugadores · ${data.altas} altas · ${data.bajas} bajas${data.first_read ? ` · ${data.base_inicial} base inicial` : ""}`
          : `${t.name}: ${data.vinculados} vinculados · ${data.requieren_validacion} requieren validación · ${data.sin_coincidencia} sin coincidencia`);
      } else {
        toast.error(`${t.name}: ${data.status} — se conserva la plantilla anterior`);
      }
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(null); }
  };

  const removeTeam = async (t) => {
    if (!window.confirm(`¿Quitar "${t.name}" del motor RFFM? (no borra jugadores ya sincronizados)`)) return;
    await api.delete(`/rffm/teams/${t.id}`);
    load();
  };

  const unread = alerts.filter((a) => !a.read).length;

  return (
    <div className="space-y-5" data-testid="rffm-engine-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-black flex items-center gap-3" style={{ fontFamily: "Nunito" }}>
          <Satellite className="h-8 w-8 text-purple-700" /> Motor RFFM
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          Sincronización con datos públicos de la Real Federación de Fútbol de Madrid. Revisión automática diaria (madrugada, hora de Madrid).
        </p>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        {[["equipos", "Equipos"], ["informes", "Informes"], ["alertas", `Alertas${unread ? ` (${unread})` : ""}`], ["logs", "Log técnico"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-3 py-2 font-bold text-sm -mb-px border-b-2 ${tab === k ? "border-purple-700 text-purple-800" : "border-transparent text-slate-500"}`}
            data-testid={`rffm-tab-${k}`}>{l}</button>
        ))}
      </div>

      {tab === "equipos" && (
        <>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <form onSubmit={addTeam} className="grid grid-cols-1 sm:grid-cols-6 gap-3 items-end">
                <div>
                  <Label className="text-xs">Tipo</Label>
                  <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                    <SelectTrigger data-testid="rffm-form-kind"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rival">Equipo rival</SelectItem>
                      <SelectItem value="own">Equipo EF Concepción</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Nombre</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="A.D. CIRINGANILLOS" data-testid="rffm-form-name" />
                </div>
                <div>
                  <Label className="text-xs">Código RFFM</Label>
                  <Input value={form.rffm_team_code} onChange={(e) => setForm({ ...form, rffm_team_code: e.target.value })} required placeholder="769" data-testid="rffm-form-code" />
                </div>
                {form.kind === "rival" ? (
                  <div>
                    <Label className="text-xs">Rival de (equipo EF)</Label>
                    <Input value={form.equipo_ef} onChange={(e) => setForm({ ...form, equipo_ef: e.target.value })} placeholder="Aficionado A" data-testid="rffm-form-ef" />
                  </div>
                ) : (
                  <div>
                    <Label className="text-xs">Equipo de la app</Label>
                    <Select value={form.team_id} onValueChange={(v) => setForm({ ...form, team_id: v })}>
                      <SelectTrigger data-testid="rffm-form-team"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                      <SelectContent>
                        {efTeams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white font-bold" data-testid="rffm-form-add">
                  <Plus className="h-4 w-4 mr-1.5" /> Añadir
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="space-y-2">
            {teams.length === 0 && <p className="text-sm text-slate-400 text-center py-8">Añade equipos con su código RFFM (el número de la URL fichaequipo).</p>}
            {teams.map((t) => (
              <Card key={t.id} className="border-slate-200" data-testid={`rffm-team-${t.rffm_team_code}`}>
                <CardContent className="p-4 flex items-center gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-slate-900">{t.name}</div>
                    <div className="text-xs text-slate-500">
                      {t.kind === "rival" ? "Rival" : "EF Concepción"} · Código {t.rffm_team_code}
                      {t.players_count != null && ` · ${t.players_count} jugadores`}
                      {t.equipo_ef && ` · Rival de ${t.equipo_ef}`}
                      {t.last_sync_at && ` · Última sync: ${new Date(t.last_sync_at).toLocaleString("es-ES")}`}
                    </div>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_BADGE[t.last_sync_status] || "bg-slate-100"}`}>
                    {t.last_sync_status || "PENDIENTE"}
                  </span>
                  <Button size="sm" onClick={() => syncTeam(t)} disabled={busy === t.id}
                    className="bg-purple-600 hover:bg-purple-700 text-white font-bold h-8"
                    data-testid={`rffm-sync-${t.rffm_team_code}`}>
                    {busy === t.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Sincronizar</>}
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => removeTeam(t)} data-testid={`rffm-delete-${t.rffm_team_code}`}>
                    <Trash2 className="h-4 w-4 text-slate-400 hover:text-rose-600" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {tab === "informes" && (
        <>
          <Card className="border-slate-200">
            <CardContent className="p-4 flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <div className="font-bold text-slate-900 flex items-center gap-2"><FileText className="h-4 w-4 text-purple-700" /> Informes individuales RFFM</div>
                <p className="text-xs text-slate-500 mt-1">
                  Analiza jugador a jugador sus 3 últimas temporadas: ficha, calendario del equipo, actas partido a partido y estadísticas. Se procesan en cola, de uno en uno.
                </p>
              </div>
              <div className="flex gap-2 flex-wrap items-center">
                {["PENDIENTE", "PROCESANDO", "COMPLETADO", "ERROR"].map((s) => (
                  <span key={s} className={`text-[10px] font-bold px-2 py-1 rounded-full ${QUEUE_BADGE[s]}`} data-testid={`queue-count-${s}`}>
                    {s}: {queue.counts?.[s] || 0}
                  </span>
                ))}
              </div>
              <Button onClick={generatePending} disabled={busy === "pending"}
                className="bg-purple-700 hover:bg-purple-800 text-white font-bold" data-testid="rffm-generate-pending">
                {busy === "pending" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileText className="h-4 w-4 mr-1.5" /> Generar informes pendientes</>}
              </Button>
            </CardContent>
          </Card>
          <Card className="border-slate-200 overflow-hidden">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>{["Jugador", "Código RFFM", "Estado", "Progreso", "Resultado", "Fecha"].map((h) => (
                    <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-slate-500">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(queue.jobs || []).map((j) => (
                    <tr key={j.id} data-testid={`queue-job-${j.codigo_rffm}`}>
                      <td className="px-3 py-2 font-semibold max-w-[200px] truncate">{j.jugador || j.jugador_id}</td>
                      <td className="px-3 py-2">{j.codigo_rffm}</td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${QUEUE_BADGE[j.status] || "bg-slate-100"}`}>{j.status}</span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {PROGRESS_LABEL[j.progress] || j.progress}
                        {j.progress_detail && <span className="text-slate-400"> · {j.progress_detail}</span>}
                      </td>
                      <td className="px-3 py-2 max-w-[260px]">
                        {j.status === "ERROR" && <span className="text-rose-600">{j.error}</span>}
                        {j.status === "COMPLETADO" && j.summary?.seasons && (
                          <span className="text-emerald-700">
                            {j.summary.seasons.map((s) => `${s.season_label}: ${s.appearances}/${s.actas_read} actas`).join(" · ")}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">{new Date(j.created_at).toLocaleString("es-ES")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(queue.jobs || []).length === 0 && <div className="p-8 text-center text-slate-400 text-sm">Sin informes en cola.</div>}
            </CardContent>
          </Card>
        </>
      )}

      {tab === "alertas" && (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="flex justify-end p-2 border-b border-slate-100">
              <Button size="sm" variant="ghost" onClick={async () => { await api.put("/rffm/alerts/read"); load(); }} data-testid="rffm-alerts-read">
                <Bell className="h-3.5 w-3.5 mr-1.5" /> Marcar leídas
              </Button>
            </div>
            <ul className="divide-y divide-slate-100 max-h-[60vh] overflow-y-auto">
              {alerts.length === 0 && <li className="p-8 text-center text-slate-400 text-sm">Sin alertas.</li>}
              {alerts.map((a) => (
                <li key={a.id} className={`p-3 text-sm flex gap-3 ${a.read ? "opacity-60" : ""}`}>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full h-fit shrink-0 ${a.type.includes("ERROR") ? "bg-rose-100 text-rose-700" : a.type === "NUEVA_ALTA" ? "bg-emerald-100 text-emerald-700" : "bg-purple-100 text-purple-800"}`}>{a.type}</span>
                  <div className="min-w-0">
                    <div>{a.message}</div>
                    <div className="text-xs text-slate-400">{new Date(a.date).toLocaleString("es-ES")}</div>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {tab === "logs" && (
        <Card className="border-slate-200 overflow-hidden">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>{["Fecha", "Tipo", "Código", "HTTP", "Resultado", "Datos", "ms"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-slate-500">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {logs.map((l) => (
                  <tr key={l.id}>
                    <td className="px-3 py-2 whitespace-nowrap">{new Date(l.date).toLocaleString("es-ES")}</td>
                    <td className="px-3 py-2">{l.type}</td>
                    <td className="px-3 py-2">{l.rffm_code}</td>
                    <td className="px-3 py-2">{l.http ?? "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`font-bold ${l.result === "OK" ? "text-emerald-700" : "text-rose-600"}`}>{l.result}</span>
                    </td>
                    <td className="px-3 py-2">
                      {l.found != null && `${l.found} jug.`} {l.altas != null && ` +${l.altas}`} {l.bajas != null && ` -${l.bajas}`}
                      {l.vinculados != null && ` ${l.vinculados} vinc.`}
                    </td>
                    <td className="px-3 py-2">{l.duration_ms}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {logs.length === 0 && <div className="p-8 text-center text-slate-400 text-sm">Sin registros.</div>}
          </CardContent>
        </Card>
      )}
      <p className="text-[11px] text-slate-400 flex items-center gap-1.5">
        <ScrollText className="h-3.5 w-3.5" /> Solo se utiliza información pública de rffm.es, consultada desde el servidor con limitación de peticiones. Los datos manuales de scouting nunca se sobrescriben.
      </p>
    </div>
  );
}
